import { ChevronDown, ChevronUp } from "lucide-react";

type Props = {
  votesUp: number;
  votesDown: number;
  userVote: "up" | "down" | null;
  onVote: (type: "up" | "down") => void;
  disabled?: boolean;
  size?: "sm" | "md";
};

export function VoteButtons({
  votesUp,
  votesDown,
  userVote,
  onVote,
  disabled = false,
  size = "sm",
}: Props) {
  const height = size === "sm" ? 28 : 32;
  const fontSize = size === "sm" ? 11 : 13;
  const pxH = size === "sm" ? 6 : 10;
  const innerGap = size === "sm" ? 6 : 8;
  const btnGap = size === "sm" ? 6 : 8;
  const iconSize = size === "sm" ? 13 : 15;

  const upActive = userVote === "up";
  const downActive = userVote === "down";

  const baseStyle: React.CSSProperties = {
    height,
    fontSize,
    paddingLeft: pxH,
    paddingRight: pxH,
    gap: innerGap,
    borderWidth: 1,
    borderStyle: "solid",
    borderRadius: 6,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 150ms, color 150ms, border-color 150ms",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    userSelect: "none",
  };

  const upStyle: React.CSSProperties = upActive
    ? {
        ...baseStyle,
        background: "hsl(142,72%,38%)", // emerald-600
        borderColor: "hsl(142,72%,38%)",
        color: "#ffffff",
      }
    : {
        ...baseStyle,
        background: "#ffffff",
        borderColor: "hsl(210,30%,88%)",
        color: "hsl(215,16%,42%)",
      };

  const downStyle: React.CSSProperties = downActive
    ? {
        ...baseStyle,
        background: "hsl(346,77%,50%)", // rose-600
        borderColor: "hsl(346,77%,50%)",
        color: "#ffffff",
      }
    : {
        ...baseStyle,
        background: "#ffffff",
        borderColor: "hsl(210,30%,88%)",
        color: "hsl(215,16%,42%)",
      };

  function handleClick(type: "up" | "down", e: React.MouseEvent) {
    e.stopPropagation();
    if (disabled) return;
    onVote(type);
  }

  return (
    <div
      style={{ display: "inline-flex", alignItems: "center", gap: btnGap }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={(e) => handleClick("up", e)}
        disabled={disabled}
        style={upStyle}
        className={
          upActive
            ? ""
            : "hover:!bg-[hsl(142,70%,95%)] hover:!text-[hsl(142,70%,28%)] hover:!border-[hsl(142,55%,75%)]"
        }
        aria-pressed={upActive}
        aria-label="Upvote"
      >
        <ChevronUp
          style={{
            width: iconSize,
            height: iconSize,
            color: upActive ? "#ffffff" : undefined,
          }}
          strokeWidth={2}
        />
        <span>{votesUp}</span>
      </button>
      <button
        type="button"
        onClick={(e) => handleClick("down", e)}
        disabled={disabled}
        style={downStyle}
        className={
          downActive
            ? ""
            : "hover:!bg-[hsl(346,75%,96%)] hover:!text-[hsl(346,70%,40%)] hover:!border-[hsl(346,75%,82%)]"
        }
        aria-pressed={downActive}
        aria-label="Downvote"
      >
        <ChevronDown
          style={{
            width: iconSize,
            height: iconSize,
            color: downActive ? "#ffffff" : undefined,
          }}
          strokeWidth={2}
        />
        <span>{votesDown}</span>
      </button>
    </div>
  );
}
